import { HtmlHTMLAttributes, RefObject, useRef } from 'react'
import PopupContainer from '../PopupContainer'

import "./style.css"
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { useGet } from 'src/hooks/useGet'
import { GetBatchDispense } from 'src/requests/Shared'
import DataState from 'src/component/DataState/DataState'
import { Skeleton } from 'src/component/Skeleton/Skeleton'
import { Alert } from 'src/component/Alert'
import SubmitBtn from 'src/component/ActionBtn/SubmitBtn'
import { takeScreenshot } from 'src/utils'
import CardRow from 'src/component/CardRow/CardRow'

type BatchDispensePopupProps = {
    modalId: RefObject<HTMLDivElement | null>,
    recordId: number | null
    handlePrint?: () => void
}

type MedicineBatch = {
    batchNumber: string
    amount: number
    isDonated: boolean
    expiryDate: string
}

type MedicinesBatchDispense = {
    medicineName: string
    gtin: string
    totalAmount: number
    totalCost: number
    isDonated: boolean
    batches: MedicineBatch[],
}

const ModalName = "BatchDispensePopup"

const BatchDispensePopup = ({ modalId, recordId, handlePrint }: BatchDispensePopupProps) => {
    const { t } = useTranslation()
    const captureRef = useRef<HTMLDivElement | null>(null)

    const { data, isPending } = useGet<MedicinesBatchDispense[]>({
        API: GetBatchDispense,
        depArr: [recordId],
        params: [recordId],
        isNotValid: !recordId,
        defaultValue: [],
    })
    const handleScreenshot = () => {
        if (!captureRef.current) return

        takeScreenshot(captureRef?.current, {
            fileName: `request-${recordId}`,
            scale: 2
        })
    }

    return (
        <PopupContainer Name={t('BatchDispensePopup')} modalID={modalId} modalName={ModalName} recordID={recordId}>
            <DataState
                isEmpty={!data || !data.length}
                isLoading={isPending}
                emptyState={
                    <Alert variant='danger'>
                        {t("noMedicinesAvailable")}
                    </Alert>}
                skeleton={<Skeleton height={100} width="100%" />}
            >

                <div className='d-flex flex-column gap-3 py-1' ref={captureRef}>
                    <div className='request-batch-details'>
                        <CardRow title='requestNumber' value={String(recordId)} className='request-batch-number' />
                    </div>
                    {data && Array.isArray(data) && data?.map((item, idx) => (
                        <div key={idx} className='batchCardContainer'>

                            <div className='batchCardHeader'>
                                <div className='batchCardLeft'>
                                    <div className='batchNameRow'>
                                        <span className='batchMedicineName'>{item.medicineName}</span>
                                        <div className={`isDonatedStatus ${item.isDonated ? "donated" : "standered"}`}>
                                            {item.isDonated
                                                ? <><Icon icon="mdi:hand-heart-outline" fontSize={12} />{t("donated")}</>
                                                : t("standerd")
                                            }
                                        </div>
                                    </div>
                                    {
                                        item.gtin &&
                                        <span className='batchCardGtin text-capitalize'>
                                            {t("gtin")}: {item.gtin}
                                            <Icon icon="famicons:barcode-outline" fontSize={14} />
                                        </span>
                                    }
                                </div>

                                <div className='batchCardTotals'>
                                    <div className='totalBlock'>
                                        <span className='totalLabel'>{t("totalAmount")}</span>
                                        <span className='totalAmount'>{item.totalAmount.toLocaleString()} {t("units")}</span>
                                    </div>
                                    <div className='totalBlock'>
                                        <span className='totalLabel'>{t("totalCost")}</span>
                                        <span className='totalCost'>{item.totalCost.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>

                            <table className='batchTable'>
                                <thead>
                                    <tr>
                                        <th>{t("batchNumber")}</th>
                                        <th>{t("amount")}</th>
                                        <th>{t("expiryDate")}</th>
                                        <th>{t("batchType")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {item.batches.map((batch, bIdx) => (
                                        <tr key={bIdx}>
                                            <td className='col-number'>{batch.batchNumber}</td>
                                            <td className='col-amount'>{batch.amount.toLocaleString()}</td>
                                            <td className='col-expiry'>{batch.expiryDate}</td>
                                            <td className=''>
                                                <span className={`rowStatusBadge ${batch.isDonated ? "donated" : "standered"}`}>
                                                    {batch.isDonated
                                                        ? <><Icon icon="mdi:hand-heart-outline" fontSize={11} />{t("donated")}</>
                                                        : t("standerd")
                                                    }
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                        </div>
                    ))}
                </div>
            </DataState>
            <div className='d-flex gap-2 my-2'>
                {
                    handlePrint &&
                    <SubmitBtn
                        label={t('proccessed-to-print-request')}
                        onSubmit={() => { handlePrint(); modalId?.current?.click() }}
                        className='flex-grow-1'
                    />
                }
                {!isPending && data.length &&
                    <SubmitBtn
                        label={t('download-picture-of-batches')}
                        onSubmit={() => handleScreenshot()}
                        className='change-request flex-grow-1'
                    />}
            </div>
        </PopupContainer>
    )
}

export default BatchDispensePopup